import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, MapPin, DollarSign, Calendar, Zap, Package,
  Filter, X, ChevronRight, Building2, Globe, RefreshCw,
  Clock, Flame, TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const URGENCY_CONFIG = {
  hot:  { label: 'Acil', color: 'bg-red-100 text-red-700', icon: Flame },
  warm: { label: 'Normal', color: 'bg-amber-100 text-amber-700', icon: TrendingUp },
  cool: { label: 'Standart', color: 'bg-blue-100 text-blue-700', icon: Clock },
};

export default function FindBriefs() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  const fetchBriefs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (search) params.set('q', search);
      if (category) params.set('category', category);
      const res = await axios.get(`${API}/supplier/open-briefs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // API returns {items: [...], total: N} — extract items array
      setBriefs(res.data?.items || res.data || []);
    } catch (e) {
      console.error(e);
      setBriefs([]);
    } finally {
      setLoading(false);
    }
  }, [search, category, token]);

  useEffect(() => { fetchBriefs(); }, [fetchBriefs]);

  const urgency = (b) => {
    if (!b.deadline) return 'cool';
    const days = Math.ceil((new Date(b.deadline) - new Date()) / 86400000);
    if (days <= 14) return 'hot';
    if (days <= 30) return 'warm';
    return 'cool';
  };

  const formatCurrency = (val, cur) => {
    if (!val) return '—';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: cur || 'EUR', maximumFractionDigits: 0 }).format(val);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="find-briefs-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Açık Brief'ler</h1>
        <p className="text-gray-500 mt-1">Exhibitor'ların ihtiyaç duyduğu malzeme ve tedarik fırsatları</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm text-center">
          <div className="text-2xl font-bold text-gray-900">{briefs.length}</div>
          <div className="text-xs text-gray-500">Açık Fırsat</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm text-center">
          <div className="text-2xl font-bold text-red-600">{briefs.filter(b => urgency(b) === 'hot').length}</div>
          <div className="text-xs text-gray-500">Acil Brief</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm text-center">
          <div className="text-2xl font-bold text-blue-600">{new Set(briefs.map(b => b.country)).size}</div>
          <div className="text-xs text-gray-500">Ülke</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Fuar adı veya şehir ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="brief-search-input"
          />
        </div>
        <button onClick={fetchBriefs} className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition">
          <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin text-blue-500' : ''}`} />
        </button>
      </div>

      {/* Brief Cards */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : briefs.length === 0 ? (
        <div className="text-center py-20">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Şu an açık brief bulunamadı</p>
          <p className="text-xs text-gray-400 mt-1">Yakında yeni fırsatlar eklenecek</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {briefs.map(brief => {
            const urg = urgency(brief);
            const UrgIcon = URGENCY_CONFIG[urg].icon;
            return (
              <div key={brief.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-200 overflow-hidden"
                data-testid={`brief-card-${brief.id}`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 text-sm leading-tight mb-1">{brief.show}</h3>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <MapPin className="w-3 h-3" />
                        <span>{brief.city}, {brief.country}</span>
                      </div>
                    </div>
                    <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${URGENCY_CONFIG[urg].color}`}>
                      <UrgIcon className="w-3 h-3" />
                      {URGENCY_CONFIG[urg].label}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 rounded-xl p-3">
                      <div className="text-xs text-gray-400 mb-0.5">Bütçe</div>
                      <div className="font-bold text-gray-900 text-sm">{formatCurrency(brief.budget_max, brief.currency)}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <div className="text-xs text-gray-400 mb-0.5">Son Teklif</div>
                      <div className="font-bold text-gray-900 text-sm">
                        {brief.deadline ? new Date(brief.deadline).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) : '—'}
                      </div>
                    </div>
                  </div>

                  {brief.materials_needed?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {brief.materials_needed.slice(0, 3).map((m, i) => (
                        <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-full font-medium">{m}</span>
                      ))}
                      {brief.materials_needed.length > 3 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">+{brief.materials_needed.length - 3}</span>
                      )}
                    </div>
                  )}

                  {brief.exhibitor && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-3">
                      <Building2 className="w-3 h-3" />
                      <span>{brief.exhibitor}</span>
                    </div>
                  )}

                  <button
                    onClick={() => navigate(`/supplier/brief/${brief.id}`)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition"
                    data-testid={`apply-brief-${brief.id}`}
                  >
                    Teklif Ver <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
